/**
 * Minimal dependency-free line chart. The web app has no charting library
 * installed (see apps/web/package.json), and adding one just for a handful
 * of small trend lines isn't worth the bundle weight -- plain SVG covers
 * this fine.
 */

interface TimeSeriesChartProps {
  points: { label: string; value: number }[];
  color?: string;
  formatValue?: (value: number) => string;
  height?: number;
}

export function TimeSeriesChart({
  points,
  color = "#2D4A6D",
  formatValue = (v) => String(v),
  height = 160,
}: TimeSeriesChartProps) {
  const width = 560;
  const paddingX = 8;
  const paddingY = 16;

  if (points.length === 0) {
    return (
      <div className="flex h-40 items-center justify-center text-sm text-ink-soft">No data in this range yet.</div>
    );
  }

  const values = points.map((p) => p.value);
  const max = Math.max(...values, 1);
  const min = 0;

  const innerWidth = width - paddingX * 2;
  const innerHeight = height - paddingY * 2;

  const stepX = points.length > 1 ? innerWidth / (points.length - 1) : 0;

  const coords = points.map((p, i) => {
    const x = paddingX + i * stepX;
    const ratio = max === min ? 0 : (p.value - min) / (max - min);
    const y = paddingY + innerHeight * (1 - ratio);
    return { x, y, point: p };
  });

  const linePath = coords.map((c, i) => `${i === 0 ? "M" : "L"} ${c.x.toFixed(1)} ${c.y.toFixed(1)}`).join(" ");
  const areaPath = `${linePath} L ${coords[coords.length - 1]!.x.toFixed(1)} ${(height - paddingY).toFixed(1)} L ${coords[0]!.x.toFixed(1)} ${(height - paddingY).toFixed(1)} Z`;

  const latest = points[points.length - 1]!;

  return (
    <div>
      <div className="mb-2 flex items-baseline justify-between">
        <p className="text-xs text-ink-soft">Latest: {latest.label}</p>
        <p className="font-display text-lg text-ink">{formatValue(latest.value)}</p>
      </div>
      <svg viewBox={`0 0 ${width} ${height}`} className="w-full" role="img" aria-label="Trend chart">
        <path d={areaPath} fill={color} fillOpacity={0.08} stroke="none" />
        <path d={linePath} fill="none" stroke={color} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
        {coords.map((c, i) => (
          <circle key={i} cx={c.x} cy={c.y} r={2.5} fill={color} />
        ))}
      </svg>
      <div className="mt-1 flex justify-between text-[11px] text-ink-soft">
        <span>{points[0]!.label}</span>
        {points.length > 2 && <span>{points[Math.floor(points.length / 2)]!.label}</span>}
        <span>{points[points.length - 1]!.label}</span>
      </div>
    </div>
  );
}
